import React, { useState, useEffect } from 'react';
import { 
  Shield, Lock, Eye, Server, Database, CheckCircle,
  AlertTriangle, FileText, Globe, Key, Users, Activity
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const SecurityCenter = () => {
  const [securityData, setSecurityData] = useState(null);
  const [isolationData, setIsolationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchSecurityData();
  }, []);

  const fetchSecurityData = async () => {
    try {
      const [postureRes, isolationRes] = await Promise.all([
        fetch(`${API_URL}/api/security/posture`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/security/data-isolation`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      setSecurityData(await postureRes.json());
      setIsolationData(await isolationRes.json());
    } catch (error) {
      console.error('Error fetching security data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="security-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="security-center-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Security & Data Privacy</h1>
          <p className="text-slate-400 dark:text-gray-400 mt-1">
            Your data never leaves your infrastructure
          </p>
        </div>
        <div className="flex items-center gap-2 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 px-4 py-2 rounded-full">
          <Shield className="w-5 h-5" />
          <span className="font-medium">All Systems Secure</span>
        </div>
      </div>

      {/* Key Security Promise */}
      <div className="bg-blue-600 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 text-white">
        <div className="flex items-start gap-6">
          <div className="p-4 bg-white/20 rounded-xl">
            <Lock className="w-10 h-10" />
          </div>
          <div>
            <h2 className="text-2xl font-bold mb-2">Your Data Never Leaves Your Infrastructure</h2>
            <p className="text-white/80 text-lg mb-4">
              CARTIEAI is software you deploy and control. We provide the application - you provide the infrastructure.
              At no point does your cost data touch our servers or any external system.
            </p>
            <div className="flex flex-wrap gap-3">
              <span className="bg-white/20 px-3 py-1 rounded-full text-sm">No External Data Sharing</span>
              <span className="bg-white/20 px-3 py-1 rounded-full text-sm">No Telemetry</span>
              <span className="bg-white/20 px-3 py-1 rounded-full text-sm">No Third-Party Access</span>
              <span className="bg-white/20 px-3 py-1 rounded-full text-sm">100% Customer-Controlled</span>
            </div>
          </div>
        </div>
      </div>

      {/* Data Flow Visualization */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">How Your Data Flows</h3>
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 text-center p-4 bg-emerald-50 dark:bg-blue-900/20 rounded-xl">
            <Globe className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
            <p className="font-medium text-gray-900 dark:text-white">Your Cloud Providers</p>
            <p className="text-sm text-slate-400 dark:text-gray-400">AWS · Azure · GCP · Snowflake · Databricks · DigitalOcean</p>
          </div>
          <div className="text-4xl text-gray-300 dark:text-slate-400">→</div>
          <div className="flex-1 text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border-2 border-green-500">
            <Server className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <p className="font-medium text-gray-900 dark:text-white">CARTIEAI (read-only)</p>
            <p className="text-sm text-slate-400 dark:text-gray-400">Credentials in-memory only — never persisted</p>
          </div>
          <div className="text-4xl text-gray-300 dark:text-slate-400">→</div>
          <div className="flex-1 text-center p-4 bg-emerald-50 dark:bg-cyan-900/20 rounded-xl">
            <Database className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
            <p className="font-medium text-gray-900 dark:text-white">Your Database</p>
            <p className="text-sm text-slate-400 dark:text-gray-400">MongoDB / PostgreSQL</p>
          </div>
          <div className="text-4xl text-gray-300 dark:text-slate-400">→</div>
          <div className="flex-1 text-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-xl">
            <Users className="w-8 h-8 text-orange-500 mx-auto mb-2" />
            <p className="font-medium text-gray-900 dark:text-white">Your Users</p>
            <p className="text-sm text-slate-400 dark:text-gray-400">Secure access</p>
          </div>
        </div>
        <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-xl flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0" />
          <p className="text-red-700 dark:text-red-400">
            <strong>No data ever goes to CARTIEAI servers.</strong> We literally cannot see your data because it never touches our systems.
          </p>
        </div>
      </div>

      {/* Compliance Frameworks */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {securityData?.compliance_frameworks?.map((framework, index) => (
          <div 
            key={index}
            className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-lg text-center"
            data-testid={`compliance-${framework.name}`}
          >
            <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <h4 className="font-bold text-gray-900 dark:text-white">{framework.name}</h4>
            <span className={`text-sm ${framework.status === 'Compliant' ? 'text-green-500' : 'text-emerald-500'}`}>
              {framework.status}
            </span>
          </div>
        ))}
      </div>

      {/* Data Isolation Checks */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Eye className="w-6 h-6 text-emerald-500" />
          Data Isolation Verification
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {isolationData?.checks?.map((check, index) => (
            <div 
              key={index}
              className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
            >
              <CheckCircle className={`w-5 h-5 ${check.status === 'PASS' ? 'text-green-500' : 'text-red-500'}`} />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{check.check}</p>
                <p className="text-sm text-slate-400 dark:text-gray-400">{check.details}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* What We Never Do */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">What We NEVER Do</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {securityData?.what_we_never_do?.map((item, index) => (
            <div key={index} className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <span className="text-red-500 text-xl">✕</span>
              <span className="text-gray-700 dark:text-gray-300">{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Deployment Options */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Deployment Options</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {securityData?.deployment_options?.map((option, index) => (
            <div key={index} className="p-5 border border-gray-200 dark:border-gray-700 rounded-xl">
              <h4 className="font-bold text-gray-900 dark:text-white mb-2">{option.type}</h4>
              <p className="text-slate-400 dark:text-gray-400 text-sm mb-3">{option.description}</p>
              <div className="space-y-2">
                <p className="text-sm">
                  <span className="text-slate-400 dark:text-gray-400">Data Location:</span>{' '}
                  <span className="font-medium text-gray-900 dark:text-white">{option.data_location}</span>
                </p>
                <p className="text-sm text-slate-400 dark:text-gray-400">
                  Recommended for: {option.recommended_for?.join(', ')}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Encryption Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Key className="w-6 h-6 text-yellow-500" />
            Encryption at Rest
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-slate-400 dark:text-gray-400">Algorithm</span>
              <span className="font-medium text-gray-900 dark:text-white">{securityData?.encryption?.at_rest?.algorithm}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400 dark:text-gray-400">Key Management</span>
              <span className="font-medium text-gray-900 dark:text-white">{securityData?.encryption?.at_rest?.key_management}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400 dark:text-gray-400">Status</span>
              <span className="text-green-500 font-medium">Enabled</span>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Activity className="w-6 h-6 text-green-500" />
            Encryption in Transit
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-slate-400 dark:text-gray-400">Protocol</span>
              <span className="font-medium text-gray-900 dark:text-white">{securityData?.encryption?.in_transit?.protocol}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400 dark:text-gray-400">Certificate</span>
              <span className="font-medium text-gray-900 dark:text-white">{securityData?.encryption?.in_transit?.certificate}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400 dark:text-gray-400">Status</span>
              <span className="text-green-500 font-medium">Enabled</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityCenter;
